import { useId, useRef } from 'react';

import { navigateTabs } from './tabNavigation';

import type { KeyboardEvent, ReactElement, ReactNode } from 'react';

export interface TabItem<Id extends string> {
    id: Id;
    label: string;
    content: ReactNode;
}

/**
 * Controlled tab set following the WAI-ARIA tabs pattern: a roving tab stop on
 * the tab list, arrow-key navigation with automatic activation, and one panel
 * per tab that is hidden while inactive so ids referenced by `aria-controls`
 * stay resolvable.
 */
export function Tabs<Id extends string>({
    label,
    tabs,
    activeTab,
    onActiveTabChange,
}: {
    label: string;
    tabs: readonly TabItem<Id>[];
    activeTab: Id;
    onActiveTabChange: (tab: Id) => void;
}): ReactElement {
    const baseId = useId();
    const tabElements = useRef(new Map<Id, HTMLButtonElement>());
    const tabId = (id: Id): string => `${baseId}-tab-${id}`;
    const panelId = (id: Id): string => `${baseId}-panel-${id}`;

    const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
        const nextTab = navigateTabs(
            event.key,
            tabs.map((tab) => tab.id),
            activeTab
        );
        if (nextTab === undefined) return;
        event.preventDefault();
        onActiveTabChange(nextTab);
        tabElements.current.get(nextTab)?.focus();
    };

    return (
        <div className='tabs'>
            <div
                className='tabs__list'
                role='tablist'
                aria-label={label}
            >
                {tabs.map((tab) => {
                    const isActive = tab.id === activeTab;
                    return (
                        <button
                            key={tab.id}
                            id={tabId(tab.id)}
                            ref={(element) => {
                                if (element !== null) tabElements.current.set(tab.id, element);
                                return () => {
                                    tabElements.current.delete(tab.id);
                                };
                            }}
                            className='tabs__tab'
                            type='button'
                            role='tab'
                            aria-selected={isActive}
                            aria-controls={panelId(tab.id)}
                            tabIndex={isActive ? 0 : -1}
                            onClick={() => {
                                onActiveTabChange(tab.id);
                            }}
                            onKeyDown={handleKeyDown}
                        >
                            {tab.label}
                        </button>
                    );
                })}
            </div>
            {tabs.map((tab) => (
                <div
                    key={tab.id}
                    id={panelId(tab.id)}
                    className='tabs__panel'
                    role='tabpanel'
                    aria-labelledby={tabId(tab.id)}
                    hidden={tab.id !== activeTab}
                >
                    {tab.content}
                </div>
            ))}
        </div>
    );
}
